/**
 * Pipe Sizing Assistant
 *
 * Given a flow rate, fluid density, pipe specification, and velocity bounds,
 * suggests the optimal standard pipe size that keeps velocity within the
 * specified range.
 *
 * Velocity formula: v = (|flow| / density) / (PI * (ID/2)^2)
 *   where flow is mass flow (kg/s), density is passed as-is from fluid zone
 *   (matching calc engine convention), ID is inner diameter (m)
 */

import type { PipeSpecification, PipeSizeOption } from '@shared/hydraulic'
import { readSI } from '@shared/hydraulic'

export interface SizingSuggestion {
  edgeId: string
  currentSizeId: string | null
  suggestedSizeId: string
  suggestedSizeName: string
  velocity: number          // m/s at suggested size
  warning?: string          // set if no size meets target
}

export interface SizingResult {
  sizeId: string
  sizeName: string
  velocity: number          // m/s
  isCompromise: boolean     // true if no size fits the range exactly
}

/**
 * Suggest the optimal pipe size within velocity bounds.
 *
 * @param flow - Mass flow rate in kg/s (absolute value from calc results)
 * @param density - Fluid density (passed as-is from fluid zone, matching calc engine convention)
 * @param spec - Pipe specification with sizes array
 * @param maxVelocity - Maximum acceptable velocity in m/s (MUST be pre-converted from constraint unit)
 * @param minVelocity - Minimum acceptable velocity in m/s (default 0)
 * @returns The suggested size and computed velocity, or null if no flow or empty spec
 */
export function suggestPipeSize(
  flow: number,
  density: number,
  spec: PipeSpecification,
  maxVelocity: number,
  minVelocity: number = 0,
): SizingResult | null {
  const absFlow = Math.abs(flow)
  const volumetricFlow = absFlow / density // m^3/s

  // No meaningful flow
  if (volumetricFlow < 1e-10) return null

  // No sizes available
  if (spec.sizes.length === 0) return null

  // Sort sizes by inner_diameter ascending (smallest first)
  const sortedSizes = [...spec.sizes].sort((a, b): number => {
    return innerDiameterMeters(a) - innerDiameterMeters(b)
  })

  // Compute velocity for each size
  const sizeVelocities = sortedSizes.map((size): { size: { id: string; name: string; inner_diameter: { value: number; unit: string; }; }; velocity: number; } => {
    const idMeters = innerDiameterMeters(size)
    const area = idMeters > 0 ? Math.PI * (idMeters / 2) ** 2 : 1
    const velocity = volumetricFlow / area
    return { size, velocity }
  })

  // Find smallest size where velocity is within [minVelocity, maxVelocity]
  for (const { size, velocity } of sizeVelocities) {
    if (velocity <= maxVelocity && velocity >= minVelocity) {
      return {
        sizeId: size.id,
        sizeName: size.name,
        velocity,
        isCompromise: false,
      }
    }
  }

  // No exact fit — find size closest to the midpoint of the velocity range
  const midpoint = (minVelocity + maxVelocity) / 2
  let bestMatch = sizeVelocities[0]!
  let bestError = Math.abs(bestMatch.velocity - midpoint)

  for (const sv of sizeVelocities) {
    const error = Math.abs(sv.velocity - midpoint)
    if (error < bestError) {
      bestMatch = sv
      bestError = error
    }
  }

  return {
    sizeId: bestMatch.size.id,
    sizeName: bestMatch.size.name,
    velocity: bestMatch.velocity,
    isCompromise: true,
  }
}

/** Convert inner diameter to meters. #850: `readSI` is the one length table. */
function innerDiameterMeters(size: PipeSizeOption): number {
  return size.inner_diameter ? readSI(size.inner_diameter, 'length') : 0
}
